import { useState, useEffect, createContext, useContext } from "react";
import { isBrowser } from "./Browser";


export const getSessionFromStorage = () => {
  if (!isBrowser()) {
    return {};
  }
  return JSON.parse(localStorage.getItem("sessionContext")) || {};
};

export const useStateWithLocalStorage = localStorageKey => {
  const [value, setValue] = useState(
    JSON.parse(localStorage.getItem(localStorageKey)) || {}
  );

  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(value));
  }, [localStorageKey, value]);

  return [value, setValue];
};

/**
 * Derive `userInfo` from the server session — the authoritative identity for EVERY
 * auth path. The backend verifies Google's ID token (or Cognito) and returns
 * { email, name, given_name, family_name } on session.user (cca/security/auth.py).
 *
 * Do NOT derive userInfo from the OAuth response: `useGoogleLogin`
 * (@react-oauth/google, flow: "auth-code") resolves a CodeResponse ({code, scope}),
 * which has no `profileObj` — that field belonged to the deprecated
 * react-google-login — so `userInfo:
 * response.profileObj` was always undefined, leaving Google users logged in but with
 * no userInfo (blanking name/email in Subscriptions, ApiKey, Layout, CallComponents,
 * and preventing posthog.identify()).
 */
export const userInfoFromServerSession = (serverSessionContext) => {
  const user = serverSessionContext?.session?.user;
  if (!user?.email) {
    return {};
  }
  return {
    userInfo: {
      email: user.email,
      name: user.name || undefined,
      givenName: user.given_name || undefined,
      familyName: user.family_name || undefined,
    },
  };
};

/**
 * The session context a successful POST /a2/login becomes: what the server answered
 * (`{isAdminUser, session, account}`), marked logged in, with `userInfo` taken from the
 * server session. Handing the result to `setSessionContext` (containers/App.js) is what
 * stores it, mirrors the session cookie and identifies the user to PostHog.
 *
 * `preLoginContext` is whatever the caller wants kept alongside it. LoginCard passes the
 * context it authenticated with, which is why a Google or test login's stored context
 * still carries its `google` / `test` entry. Anything passed here ends up in
 * localStorage, so the password sign-in passes nothing.
 */
export const sessionContextFromLogin = (serverSessionContext, preLoginContext = {}) => ({
  ...preLoginContext,
  ...serverSessionContext,
  isLoggedIn: true,
  ...userInfoFromServerSession(serverSessionContext),
});

export const SessionContext = createContext({});

export const useSessionContext = () => {
  return useContext(SessionContext);
};
